import { InterviewAssessment, PersonaProfile } from "@type/assessments";
import { useEffect, useState } from "react";
import { Tabs, TabsContent } from "@component/tabs";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@component/select";
import { formatHumanReadable } from "@util/date";
import { Edit } from "lucide-react";
import { createAssessment, getAssessmentDetail, updateAssessment } from "@service/assessment";
import { useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import BreadcrumbNav from "@component/breadcrumb-nav";
import AssessmentTabsNavigation from "./assessment-tabs";
import JobLinkedAssessmentSection from "./job-linked";
import TestInterviewButton from "./test-interview-button";
import InterviewSettingsSectionV2 from "./interview-setting-section-v2";
import InterviewContextSection from "./interview-context-section";
import InterviewKnowledgeBaseSection from "./interview-knowledge-base-section";
import InterviewSectionsComponent from "./interview-section-v2";
import { useSubscription } from "@context/subscription_context";

interface Props {
    isEditMode?: boolean;
    onStartTestInterview: (persona: PersonaProfile, interviewAssessment: InterviewAssessment) => void;
}

const CLOSING_STATEMENT_DEFAULT = "Okay, Thank you for your time and thoughtful responses. The hiring team will carefully review our conversation, and they will be in touch with you regarding the next steps in the process. Have a great day!";

export default function InterviewFormV2(props: Props) {
    const { assessment_id } = useParams();
    const snackbar = useSnackbar();
    // Edit/create interview assessment is blocked once the workspace is
    // restricted (trial expired + past grace) or the trial AI-assessment quota
    // is reached.
    const { isAssessmentActionBlocked } = useSubscription();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "details");
    const [isLoadingAssessment, setIsLoadingAssessment] = useState<boolean>(false);
    const [mode, setMode] = useState<"view" | "edit">(props.isEditMode ? "view" : "edit");
    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
    const [interviewSectionsValid, setInterviewSectionsValid] = useState(true);
    const [originalData, setOriginalData] = useState<InterviewAssessment>({
        type: "Interview",
        status: "Active",
        content: {
            interviewName: "",
            description: "",
            maxDuration: 0,
            customGreeting: "",
            phoneCallGreetingEnabled: true,
            phoneCallGreeting: "",
            interviewContext: "",
            aiName: "",
            interviewLanguage: "en-US",
            voice: "male",
            idleTimeoutMessage: "",
            closingStatement: CLOSING_STATEMENT_DEFAULT,
            sections: [],
            category: "None",
            type: "practice",

            interviewerPersona: "",
            toneAndCommunication: {
                enabled: false,
                selectedTones: [],
                additionalPreferences: "",
            },
            interviewFlowPrinciples: {
                enabled: false,
                selectedPrinciples: [],
                additionalGuidelines: "",
            },
        },
    });
    const [formData, setFormData] = useState<InterviewAssessment>({
        type: "Interview",
        status: "Active",
        content: {
            interviewName: "",
            description: "",
            maxDuration: 0,
            maxDurationInMinutes: 0,
            customGreeting: "",
            phoneCallGreetingEnabled: true,
            phoneCallGreeting: "",
            interviewContext: "",
            aiName: "",
            interviewLanguage: "en-US",
            voice: "male",
            idleTimeoutMessage: "",
            closingStatement: CLOSING_STATEMENT_DEFAULT,
            sections: [],
            category: "None",
            type: "official",

            interviewerPersona: "",
            toneAndCommunication: {
                enabled: false,
                selectedTones: [
                    "professional-clear-approachable"
                ],
                additionalPreferences: "",
            },
            interviewFlowPrinciples: {
                enabled: false,
                selectedPrinciples: [
                    "open-ended-natural-flow"
                ],
                additionalGuidelines: "",
            },
        },
    });

    const onLoadAssessmentDetail = (assessment_id: string) => {
        getAssessmentDetail(assessment_id).then((resp) => {
            if (resp.status === 200) {
                setFormData({
                    ...resp.data.data,
                    content: {
                        ...resp.data.data.content,
                        maxDurationInMinutes: resp.data.data.content.maxDuration ? Math.ceil(resp.data.data.content.maxDuration / 60) : 0,
                        maxDuration: resp.data.data.content.maxDuration || 0,
                    }
                });
                setOriginalData(resp.data.data);
            }
        }).catch((error) => {
            snackbar.showError("Failed to fetch screening details. Please try again.");
            catchErrorLog(`Failed to fetch assessment details for id ${assessment_id}, error: ${error}`);
        }).finally(() => {
            setIsLoadingAssessment(false);
        })
    }

    useEffect(() => {
        if (props.isEditMode && assessment_id) {
            setIsLoadingAssessment(true);
            onLoadAssessmentDetail(assessment_id);
        }
    }, [props.isEditMode, assessment_id])

    const handleCancel = () => {
        setMode("view");
    }

    const handleSave = () => {
        if (!interviewSectionsValid) {
            snackbar.showError("Please fill in all the required fields before saving.");
            // Scroll to the interview sections component
            const sectionsElement = document.querySelector('[data-pol-id="yr9hgy"]');
            if (sectionsElement) {
                sectionsElement.scrollIntoView({ behavior: "smooth" });
            }
            return;
        }

        if (props.isEditMode) {
            updateAssessment(assessment_id as string, {
                ...formData,
                content: {
                    ...formData.content,
                    maxDuration: formData.content.maxDurationInMinutes ? formData.content.maxDurationInMinutes * 60 : formData.content.maxDuration,
                    maxDurationInMinutes: formData.content.maxDurationInMinutes ? formData.content.maxDurationInMinutes : Math.ceil((formData.content.maxDuration || 0) / 60),
                    // Ensure sections are saved in the correct format
                    sections: formData.content.sections.map((section) => {
                        return {
                            ...section,
                            duration: section.duration_in_minutes ? section.duration_in_minutes * 60 : section.duration,
                            duration_in_minutes: section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60),
                        }
                    })
                }
            }).then((resp) => {
                if (resp.success) {
                    snackbar.showSuccess("Screening updated successfully.");
                }
            }).catch((error) => {
                snackbar.showError("Failed to update screening. Please try again.");
                catchErrorLog(`Failed to update assessment for id ${assessment_id}, error: ${error}`);
            }).finally(() => {
                setIsLoadingSave(false);
                onLoadAssessmentDetail(assessment_id as string);
                setMode("view");
            })
        } else {
            createAssessment({
                ...formData,
                content: {
                    ...formData.content,
                    maxDuration: formData.content.maxDurationInMinutes ? formData.content.maxDurationInMinutes * 60 : formData.content.maxDuration,
                    maxDurationInMinutes: formData.content.maxDurationInMinutes ? formData.content.maxDurationInMinutes : Math.ceil((formData.content.maxDuration || 0) / 60),
                    // Ensure sections are saved in the correct format
                    sections: formData.content.sections.map((section) => {
                        return {
                            ...section,
                            duration: section.duration_in_minutes ? section.duration_in_minutes * 60 : section.duration,
                            duration_in_minutes: section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60),
                        }
                    })
                }
            }).then((resp) => {
                if (resp.success) {
                    snackbar.showSuccess("Screening created successfully.");
                }
            }).catch((error) => {
                snackbar.showError("Failed to create screening. Please try again.");
                catchErrorLog(`Failed to create assessment, error: ${error}`);
            }).finally(() => {
                setIsLoadingSave(false);
                setMode("view");
            });
        }

    }

    const isChanges = formData.content.interviewName !== originalData.content.interviewName ||
        formData.content.description !== originalData.content.description ||
        formData.content.maxDuration !== originalData.content.maxDuration ||
        formData.content.maxDurationInMinutes !== originalData.content.maxDurationInMinutes ||
        formData.content.customGreeting !== originalData.content.customGreeting ||
        formData.content.phoneCallGreetingEnabled !== originalData.content.phoneCallGreetingEnabled ||
        formData.content.phoneCallGreeting !== originalData.content.phoneCallGreeting ||
        formData.content.interviewContext !== originalData.content.interviewContext ||
        formData.content.aiName !== originalData.content.aiName ||
        formData.content.interviewLanguage !== originalData.content.interviewLanguage ||
        formData.content.voice !== originalData.content.voice ||
        formData.content.idleTimeoutMessage !== originalData.content.idleTimeoutMessage ||
        JSON.stringify(formData.content.sections) !== JSON.stringify(originalData.content.sections) ||
        formData.status !== originalData.status ||
        formData.content.category !== originalData.content.category || formData.content.type !== originalData.content.type ||
        formData.content.interviewerPersona !== originalData.content.interviewerPersona ||
        JSON.stringify(formData.content.toneAndCommunication) !== JSON.stringify(originalData.content.toneAndCommunication) ||
        JSON.stringify(formData.content.interviewFlowPrinciples) !== JSON.stringify(originalData.content.interviewFlowPrinciples);

    return (
        <div className="min-h-screen bg-white">
            <div className="w-full p-0 space-y-8">
                <div className="flex items-center justify-between">
                    <BreadcrumbNav
                        items={[
                            { label: "Screen", href: "/assess" },
                            { label: props.isEditMode ? formData.content.interviewName || "Untitled form" : "Create AI Interview" },
                        ]}
                    />
                </div>

                <Tabs value={activeTab} onValueChange={(tab) => {
                    setActiveTab(tab);
                    setSearchParams({ tab });
                }} className="w-full">
                    <AssessmentTabsNavigation />

                    {activeTab === "details" && (
                        <div className="px-0 py-0">
                            <div
                                className="flex items-start justify-between"
                                style={{ paddingBottom: "16px" }}
                            >
                                {/* Left side - Title with Status and Timestamp below */}
                                <div className="flex flex-col gap-2">
                                    {/* Title */}
                                    {mode === "edit" ? (
                                        <div className="relative min-w-[500px] w-full max-w-[800px]">
                                            <Input
                                                value={formData.content.interviewName || ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, content: { ...formData.content, interviewName: e.target.value.slice(0, 80) } })
                                                }
                                                maxLength={80}
                                                placeholder="Enter screening title..."
                                                className="text-2xl font-semibold border border-gray-300 rounded-md px-3 py-2 pr-28 h-auto focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 hover:border-gray-400 transition-colors w-full bg-white"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{(formData.content.interviewName || "").length}/80 characters</span>
                                        </div>
                                    ) : (
                                        <h1 className="text-2xl font-bold">{formData.content.interviewName}</h1>
                                    )}

                                    {/* Status and Timestamp row */}
                                    <div className="flex items-center gap-4">
                                        {/* Status */}
                                        {mode === "edit" ? (
                                            <Select
                                                value={formData.status}
                                                onValueChange={(value) =>
                                                    setFormData({ ...formData, status: value })
                                                }
                                            >
                                                <SelectTrigger className="w-32">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className={`w-2 h-2 rounded-full ${formData.status === "Active"
                                                                ? "bg-green-500"
                                                                : formData.status === "Draft"
                                                                    ? "bg-yellow-500"
                                                                    : "bg-gray-400"
                                                                }`}
                                                        />

                                                        <span>{formData.status}</span>
                                                    </div>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Active">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-green-500" />
                                                            Active
                                                        </div>
                                                    </SelectItem>
                                                    <SelectItem value="Draft">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                                            Draft
                                                        </div>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-none ${formData.status === "Active"
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-yellow-100 text-yellow-800"
                                                    }`}
                                            >
                                                {formData.status}
                                            </span>
                                        )}

                                        {/* Timestamp */}
                                        <span className="text-sm font-normal text-gray-600">
                                            Last Updated {formatHumanReadable(formData?.updated_at || "", true)}
                                        </span>
                                    </div>
                                </div>

                                {/* Edit/Action Buttons aligned with title */}
                                <div className="flex items-center gap-3">
                                    <TestInterviewButton onStartTestInterview={props.onStartTestInterview} data={formData} />
                                    {mode === "view" ? (
                                        <>
                                            <Button
                                                variant="outline"
                                                onClick={() => setMode("edit")}
                                                className="flex items-center gap-2 font-medium"
                                                disabled={formData.is_default || isAssessmentActionBlocked}
                                                title={isAssessmentActionBlocked ? "Your trial has expired. Upgrade to edit screenings." : undefined}
                                            >
                                                <Edit className="h-4 w-4" />
                                                Edit
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Test Interview Button - Only for Interview Types (Edit Mode) */}
                                            {props.isEditMode && (
                                                <Button
                                                    variant="outline"
                                                    onClick={handleCancel}
                                                    disabled={isLoadingSave}
                                                    className="font-medium"
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                            <Button
                                                onClick={handleSave}
                                                disabled={isLoadingSave || !isChanges || !formData.content.description?.trim()}
                                                className="bg-blue-600 hover:bg-blue-700 font-medium"
                                            >
                                                {isLoadingSave ? "Saving..." : "Save Changes"}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <TabsContent value="details" className="space-y-6 pt-0 mt-0">
                        {!isLoadingAssessment && (
                            <>
                                <div
                                    className="px-8"
                                    style={{ paddingRight: "0px", paddingLeft: "0px" }}
                                >
                                    <InterviewSettingsSectionV2
                                        mode={mode}
                                        data={formData}
                                        onDataChange={(data) => setFormData((prev) => ({ ...prev, ...data } as InterviewAssessment))}
                                    />
                                </div>
                                <div
                                    className="px-8"
                                    style={{ paddingRight: "0px", paddingLeft: "0px" }}
                                >
                                    <InterviewContextSection
                                        mode={mode}
                                        data={formData}
                                        onDataChange={(data) => setFormData((prev) => ({ ...prev, ...data } as InterviewAssessment))}
                                    />
                                </div>

                                <div
                                    className="px-8"
                                    style={{ paddingRight: "0px", paddingLeft: "0px" }}
                                >
                                    <InterviewKnowledgeBaseSection
                                        mode={mode}
                                        data={formData}
                                        onDataChange={(data) => setFormData((prev) => ({ ...prev, ...data } as InterviewAssessment))}
                                    />
                                </div>

                                <div
                                    className="px-8"
                                    style={{ paddingRight: "0px", paddingLeft: "0px" }}
                                >
                                    <InterviewSectionsComponent
                                        mode={mode}
                                        data={formData}
                                        onDataChange={(data) => setFormData((prev) => ({ ...prev, ...data } as InterviewAssessment))}
                                        onValidationChange={(isValid, errors) => {
                                            setInterviewSectionsValid(isValid);
                                            console.error(`Interview sections validation errors: ${JSON.stringify(errors)}`);
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </TabsContent>

                    {/* Jobs Tab Content */}
                    <TabsContent value="jobs" className="space-y-6">
                        <JobLinkedAssessmentSection assessment_id={formData.id || ""} />
                    </TabsContent>
                </Tabs>

            </div>
        </div>
    )
}